/**
 * @file NumberPorting.utils.ts
 * @description Utility functions and constants for the Number Porting (MNP) process.
 */

import { PHONE_REGEX } from "@/utils/constants";

/**
 * Standard prefix for Swiss phone numbers in the UI.
 */
const numberPrefix = "+41 (0) ";

/**
 * MNP Contract Types (Postpaid vs Prepaid).
 */
export const mnpContractType = {
  postpaid: { id: 1, description: "postpaid" },
  prepaid: { id: 2, description: "prepaid" },
} as const;

/**
 * MNP Timing options (After contract ends or premature/before).
 */
export const mnpTime = {
  after: { id: 2, description: "after" },
  before: { id: 3, description: "before" },
} as const;

export const SWYPE_PREPAID_PROVIDER_ID = 32;
export const SWYPE_PREPAID_PROVIDER_NAME = "swype prepaid";
export const NUMBER_PREFIX = numberPrefix;

/**
 * Formats a phone number string based on the standard Swiss format and regex.
 *
 * @param input - The current raw input value.
 * @param oldInput - The previous input value to fall back to if the prefix is missing.
 * @returns Formatted phone number string.
 */
export const formatNumber = (input: string, oldInput = "") => {
  // Ensure the prefix is always present; otherwise, revert to the old input.
  if (input.indexOf(numberPrefix) !== 0) {
    return oldInput;
  } else {
    const match = input.match(PHONE_REGEX);
    const formatted = match
      ? match
          .slice(1)
          .filter((x) => x)
          .join(" ")
      : input;

    // Limit length to avoid excessive input.
    return formatted.substring(0, 32);
  }
};

/**
 * Formats a MSISDN for API consumption.
 * Strips the international prefix (+41) and ensures it's a 10-digit string starting with 0.
 *
 * @param input - The formatted phone number from the UI.
 * @returns A 10-digit MSISDN string (e.g., "0791234567").
 */
export const formatMsisdnForApi = (input: string): string => {
  // Remove international prefix (+41 (0) or +41)
  const withoutIntl = input.replace(/^(\+41\s*\(0\)\s*|\+41\s*)/, "");
  // Remove all non-digit characters
  const digits = withoutIntl.replace(/\D/g, "");
  // Ensure it starts with '0'
  const msisdn = digits.startsWith("0") ? digits : `0${digits}`;

  // Return exactly 10 digits
  return msisdn.slice(0, 10);
};
